// src/utils/changeColumnDefs.ts
import { ColDef } from "ag-grid-community";
import { ChangeData } from "./changeTypes"; // create and import the type
const isAdmin = localStorage.getItem("isAdmin") === "true";
const isSuperAdmin = localStorage.getItem("superadmin") === "true";

const formatDateUTC3 = (date: Date | string | null): string => {
  if (!date) return "";

  const d = typeof date === "string" ? new Date(date.replace(" ", "T")) : date;

  return d.toLocaleString("en-GB", {
    timeZone: "Etc/GMT-3", // ✅ corrected
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
const getColumnDefs = (
  handleEdit: (data: ChangeData) => void,
  handleDelete: (ticketId: string) => void
): ColDef<ChangeData>[] =>
  [
    {
      field: "ticketId",
      headerName: "Ticket ID",
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    { field: "status", headerName: "Status" },
    { field: "phase", headerName: "Phase" },

    isAdmin && { field: "cabapproval", headerName: "CAB Approval" },
    isAdmin && { field: "customerapproval", headerName: "Customer Approval" },
    isAdmin && { field: "changetemplate", headerName: "Change Template" },
    isAdmin && { field: "typeofchange", headerName: "Type of Change" },
    { field: "affectedCustomer", headerName: " Customer" },
    { field: "affectedNetwork", headerName: " Network" },
    { field: "affectedSite", headerName: " Site" },
    { field: "affectedLink", headerName: " Link" },
    {
      field: "affectedAntenna",
      headerName: "Antenna",
    },
    { field: "asset", headerName: "Asset" },
    {
      field: "changeStartDatetime",
      headerName: "Planned Start Datetime",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.changeStartDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.changeStartDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          // Strip time from both values
          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0;
        },
      },
    },
    {
      field: "changeCloseDatetime",
      headerName: "Planned End Datetime",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.changeCloseDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.changeCloseDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          // Strip time from both values
          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0; // dates are equal
        },
      },
    },
    {
      field: "summary",
      headerName: "Summary",
      filter: "agTextColumnFilter",
      valueFormatter: (params: { value: string }) => {
        const div = document.createElement("div");
        div.innerHTML = params.value;
        return div.textContent || div.innerText || "";
      },
    },
    {
      field: "description",
      headerName: "Description",
      filter: "agTextColumnFilter",
      valueFormatter: (params: { value: string }) => {
        const div = document.createElement("div");
        div.innerHTML = params.value;
        return div.textContent || div.innerText || "";
      },
    },
    isAdmin && {
      field: "risk",
      headerName: "Risk",
      filter: "agTextColumnFilter",
      valueFormatter: (params: { value: string }) => {
        const div = document.createElement("div");
        div.innerHTML = params.value;
        return div.textContent || div.innerText || "";
      },
    },
    isAdmin && {
      field: "rollbackstrategy",
      headerName: "Roll Back strategy",
      valueFormatter: (params: { value: string }) => {
        const div = document.createElement("div");
        div.innerHTML = params.value;
        return div.textContent || div.innerText || "";
      },
    },
    isAdmin && {
      field: "requiredresources",
      headerName: "Required resources",
      valueFormatter: (params: { value: string }) => {
        const div = document.createElement("div");
        div.innerHTML = params.value;
        return div.textContent || div.innerText || "";
      },
    },
    {
      field: "solution",
      headerName: "Solution",
      filter: "agTextColumnFilter",
      valueFormatter: (params: { value: string }) => {
        const div = document.createElement("div");
        div.innerHTML = params.value;
        return div.textContent || div.innerText || "";
      },
    },
    isAdmin && {
      field: "imapctchangeStartDatetime",
      headerName: "Impact Planned Start Date/time",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.imapctchangeStartDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.imapctchangeStartDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          // Strip time from both values
          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0; // dates are equal
        },
      },
    },
    isAdmin && {
      field: "impactchangeCloseDatetime",
      headerName: "Impact Planned End Date/time",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.impactchangeCloseDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.impactchangeCloseDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0;
        },
      },
    },
    isAdmin && {
      field: "changeResolutionTime",
      headerName: "Impact  Period",
      filter: "agNumberColumnFilter",
      valueGetter: (params: {
        data: { changeResolutionTime: any };
      }): number => {
        const val = params.data?.changeResolutionTime;
        if (!val) return 0;

        // Match a number with optional minus and optional decimal point
        const match = val.match(/-?\d+(\.\d+)?/);
        const num = match ? parseFloat(match[0]) : 0;

        return isNaN(num) ? 0 : num;
      },
      valueFormatter: (params: { data: { changeResolutionTime: any } }) => {
        return params.data?.changeResolutionTime ?? "";
      },
    },

    isAdmin && { field: "responsibleTeam", headerName: "Responsible Team" },
    isAdmin && { field: "responsiblePerson", headerName: "Responsible Person" },
    { field: "username", headerName: "User" },
    isAdmin && {
      field: "createdby",
      headerName: "Created By",
      filter: "agMultiColumnFilter",
    },
    isAdmin && {
      field: "closedby",
      headerName: "Closed By",
      filter: "agMultiColumnFilter",
    },
    { field: "priority", headerName: "Priority" },
    //missing closure code
    { field: "closureCode", headerName: "Closure Code" },
    //missing change creation date/time
    //missing change closure date/time

    isAdmin && {
      field: "ticketCreationDatetime",
      headerName: "Ticket Creation Datetime",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.ticketCreationDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.ticketCreationDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          // Strip time from both values
          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0; // dates are equal
        },
      },

      sort: "desc",
      sortIndex: 1,
    },
    isAdmin && {
      field: "ticketClosureDatetime",
      headerName: "Ticket Closure Datetime",
      filter: "agDateColumnFilter",
      valueGetter: (params: any) => {
        const raw = params.data?.ticketClosureDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      filterValueGetter: (params: any) => {
        const raw = params.data?.ticketClosureDatetime;
        return raw ? new Date(raw.replace(" ", "T")) : null;
      },
      valueFormatter: (params: any) => formatDateUTC3(params.value),

      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          // Strip time from both values
          const cellDate = new Date(
            cellValue.getFullYear(),
            cellValue.getMonth(),
            cellValue.getDate()
          );

          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0; // dates are equal
        },
      },
    },
    isAdmin && {
      field: "relatedobject",
      headerName: "Related Object",
      filter: "agMultiColumnFilter",
    },

    {
      colId: "actions",
      headerName: "Actions",
      cellRenderer: (params: any) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <button style={{}} onClick={() => handleEdit(params.data)}>
            Edit
          </button>
          {isSuperAdmin && (
            <button onClick={() => handleDelete(params.data.ticketId)}>
              Delete
            </button>
          )}
        </div>
      ),
      pinned: "right",
      pivot: true,
      flex: 1,
    },
  ].filter(Boolean) as ColDef<ChangeData>[];

export default getColumnDefs;
